import { createElement, useState } from "react";
import { Trans } from "react-i18next";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { getSettings } from "@common/settingsProvider";
import { createPdfUrl } from "@common/pdfUrlHelper";
import { downloadFile } from "@common/api/downloadFile";
import { useTranslation } from "@localization/useTranslation";
import { useUserContext } from "../auth/userContextProvider/UserContextProvider";
import { useUrlProvider } from "../useUrlProvider";
import { InternalAssessmentApiRow } from "./InternalAssessments.types";

export function useDownloadInternalAssessmentReport() {
    const { translateDate, translateString } = useTranslation();
    const { language } = useUserContext();
    const snackbar = useDotLegalSnackbar();
    const { getInternalAssessmentQuestionnaireResponseReport, getQuestionnaireResponseExternalreport } = useUrlProvider();

    const [downloadingPdfIds, setDownloadingPdfIds] = useState<Array<string>>([]);

    const onDownloadReportAsPdf = async (row: InternalAssessmentApiRow, isSimpleReport: boolean, showRecommendations: boolean) => {
        // Functional updates, because several downloads can be in flight at once and each finishes on its own schedule
        setDownloadingPdfIds((ids) => [...ids, row.id]);

        let url = isSimpleReport ? getExternalPdfUrl(row, true, showRecommendations) : getReportPdfUrl(row, true, showRecommendations);
        url = createPdfUrl(url, language);

        const fileName = `${row.name}_${row.sender}_${translateDate(row.deadline)}.pdf`;
        await downloadFile(getSettings().apiBaseUrl + "/pdf/generate?noMargin=true&url=" + url, fileName);

        stopDownloadingPdf(row.id);
        snackbar.show(createElement(Trans, { i18nKey: "pdfDownloadComplete", values: { fileName: fileName } }));
    };

    function stopDownloadingPdf(internalAssessmentId: string) {
        setDownloadingPdfIds((ids) => ids.filter((id) => id !== internalAssessmentId));
    }

    function isDownloadingPdf(internalAssessmentId: string) {
        return downloadingPdfIds.includes(internalAssessmentId);
    }

    function getReportPdfUrl(row: InternalAssessmentApiRow, pdf: boolean, showRecommendations: boolean) {
        const respondent = translateString("theRespondent").toLowerCase();

        return getInternalAssessmentQuestionnaireResponseReport(
            row.questionnaireResponseId,
            row.deadline,
            row.sender,
            row.name,
            pdf,
            showRecommendations,
            respondent
        );
    }

    function getExternalPdfUrl(row: InternalAssessmentApiRow, pdf: boolean, showRecommendations: boolean) {
        const respondent = translateString("theRespondent").toLowerCase();

        return getQuestionnaireResponseExternalreport(row.responseExternalId, row.deadline, row.sender, respondent, row.name, pdf, showRecommendations, false);
    }

    return {
        onDownloadReportAsPdf,
        getReportPdfUrl,
        isDownloadingPdf,
    };
}
